import { ContentfulProvider } from '@pcln/contentful-components'
import pclnExperimentation from '@pcln/experimentation'
import { FeatureProvider } from '@pcln/feature-components'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { type GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import Cobrand from '@/components/Cobrand'
import Layout from '@/components/Layout'
import Meta from '@/components/Meta'
import Packages from '@/components/Packages'
import { SearchFormAutocompleteWiring } from '@/components/SearchFormAutocompleteWiring/SearchFormAutocompleteWiring'
import TmsScript from '@/components/TmsScript/TmsScript'
import DEFAULT_META_TAGS from '@/constants/meta'
import useCanonicalUrl from '@/hooks/useCanonicalUrl'
import type { ContentPageProps, CustomContext } from '@/types'

import setiService from '../server/middleware/seti'
import prepopulatePackagesValues from '../server/prepop/packages'
import getContentfulData from '../server/utils/get-contentful-data'
import oktaJwtVerify from '../server/utils/okta-jwt-verifier'
import fetchPCLNHeaderFooterContent from '../server/utils/pcln-header-footer'
import retrieveContentfulSecrets from '../server/utils/retrieve-contentful-secrets'
import generateBootstrapData from '../utils/generate-bootstrap-data'
import mapSetiResponse from '../utils/map-seti-response'
import { fireHotelListingsRerouteImpressionIfFinal } from '../utils/pkg-hotel-listings-reroute-impression'
import {
  PKG_XSELL_ERROR_PERSIST_SEARCH_CRITERIA_SETI,
  readPkgSessionRecoveryFromStorage,
  shouldApplyPkgRecoveryForXsell,
} from '../utils/pkg-session-recovery'

function getRedirectUrl(
  router: ReturnType<typeof useRouter>,
  seti: Record<string, string> | undefined,
): string | null {
  const redirectSource = router.query['redirect-source']
  const isRedirectSourceError = typeof redirectSource === 'string' && redirectSource.toLowerCase() === 'error'

  if (!isRedirectSourceError) {
    return null
  }

  const payload = readPkgSessionRecoveryFromStorage()
  if (payload?.isAdvancedSearch !== true) {
    return null
  }
  if (!shouldApplyPkgRecoveryForXsell(payload, seti)) {
    return null
  }

  const message = typeof router.query['message'] === 'string' ? router.query['message'] : undefined
  return `/shop/search?redirect-source=error${message ? `&message=${encodeURIComponent(message)}` : ''}`
}

function VacationPackages({ content, headerHTML, footerHTML, bootstrapData }: ContentPageProps) {
  const canonicalUrl = useCanonicalUrl()
  const [queryClient] = useState(() => new QueryClient())
  const router = useRouter()
  const [showPageContent, setShowPageContent] = useState(!bootstrapData.deferPackagesLandingContent)

  useEffect(() => {
    if (!router.isReady) {
      return
    }

    const redirectSource = router.query['redirect-source']
    const isRedirectSourceError =
      typeof redirectSource === 'string' && redirectSource.toLowerCase() === 'error'
    if (!isRedirectSourceError) {
      return
    }

    const payload = readPkgSessionRecoveryFromStorage()
    if (payload?.containsXSell !== true) {
      return
    }

    pclnExperimentation.fireImpression(PKG_XSELL_ERROR_PERSIST_SEARCH_CRITERIA_SETI)
  }, [router.isReady, router.asPath, router.query])

  useEffect(() => {
    if (!router.isReady) {
      return
    }

    // Bind via arrow so fireImpression keeps pclnExperimentation as `this` (__isIdleQueue).
    fireHotelListingsRerouteImpressionIfFinal({
      fireImpression: (code) => pclnExperimentation.fireImpression(code),
      redirectSource: router.query['redirect-source'],
      message: router.query['message'],
      willHandOffToShopSearch: Boolean(getRedirectUrl(router, bootstrapData.seti)),
    })
  }, [router, router.isReady, router.asPath, router.query, bootstrapData.seti])

  useEffect(() => {
    if (!router.isReady) return

    const redirectUrl = getRedirectUrl(router, bootstrapData.seti)
    if (redirectUrl) {
      void router.replace(redirectUrl)
      return
    }
    queueMicrotask(() => {
      setShowPageContent(true)
    })
  }, [router, router.isReady, router.asPath, bootstrapData.seti])

  return (
    <>
      <Meta
        description={DEFAULT_META_TAGS.description.vacationpackages}
        pageTitle={DEFAULT_META_TAGS.title.vacationpackages}
        canonicalUrl={canonicalUrl}
      />
      {showPageContent ? (
        <>
          <TmsScript product='packages' />
          <FeatureProvider features={bootstrapData.featureToggles}>
            <ContentfulProvider contentful={content}>
              <QueryClientProvider client={queryClient}>
                <SearchFormAutocompleteWiring>
                  <Layout headerHTML={headerHTML} footerHTML={footerHTML}>
                    <Cobrand />
                    <Packages />
                  </Layout>
                </SearchFormAutocompleteWiring>
              </QueryClientProvider>
            </ContentfulProvider>
          </FeatureProvider>
        </>
      ) : null}
    </>
  )
}

export const getServerSideProps: GetServerSideProps<ContentPageProps> = async (ctx) => {
  const { req, res } = ctx as CustomContext
  const redirectSource = ctx.query['redirect-source']
  const isPkgErrorRedirect = typeof redirectSource === 'string' && redirectSource.toLowerCase() === 'error'

  const bootstrapData = generateBootstrapData({ product: 'packages', req, res })
  const { webstats, referral, appVersion } = bootstrapData
  const { clientGUID: cguid } = webstats
  const { contentfulDeliverySecret, contentfulPreviewSecret } = retrieveContentfulSecrets()
  const isContentfulEnabled = res.locals.features?.siteExperience.isContentfulEnabled ?? true
  const [pclnHeaderFooterContent, signInStatus, { content, experimentNames }, packagesPrepopData] =
    await Promise.all([
      fetchPCLNHeaderFooterContent(req, res),
      oktaJwtVerify({ authHdr: req.headers.authorization ?? '', cguid }),
      getContentfulData({
        contentTypeId: 'pageLanding',
        contentfulDeliverySecret,
        contentfulPreviewSecret,
        product: 'Packages',
        contentPreview: ctx.query['content-preview'] === 'true',
        isContentfulEnabled,
      }),
      prepopulatePackagesValues(ctx.query),
    ])

  const setiResponseEvents = await setiService(req, {
    appVersion,
    allowSetiOverrides: true,
    webstats,
    referral,
    reqQueryParams: ctx.query,
    ...(experimentNames && {
      additionalExperiments: experimentNames,
    }),
  })
  const setiMap = mapSetiResponse(setiResponseEvents)

  const prePopData = packagesPrepopData ? { packages: packagesPrepopData } : null

  const bootstrapWithJwtSignInData = {
    ...bootstrapData,
    customer: {
      signedIn: signInStatus.signedIn ?? false,
      emailAddress: signInStatus.email ?? '',
      firstName: String(signInStatus.firstName ?? ''),
    },
  }

  return {
    props: {
      content,
      ...pclnHeaderFooterContent,
      bootstrapData: {
        ...bootstrapWithJwtSignInData,
        fullSETI: setiResponseEvents,
        seti: setiMap,
        signInStatus,
        ...(isPkgErrorRedirect && { deferPackagesLandingContent: true }),
        ...(prePopData && { prePopData }),
      },
    },
  }
}

export default VacationPackages
